// Route tables for a signed-in customer (operator) and for a child
// (family role). Plain functions returning <Route> fragments, so App.tsx
// can drop them straight into its <Routes>.

import { lazy } from "react";
import { Navigate, Route } from "react-router-dom";

import { CustomerShell } from "@/components/layout/CustomerShell";
import { CompanionShell } from "@/companion/CompanionShell";
import { LandingRedirect } from "@/companion/LandingRedirect";

import {
  AgentWorkspaceTab,
  AgentsTab,
  CCSessionsTab,
  ChatScreen,
  ChatThread,
  CodeTab,
  CompanionMemory,
  CompanionMeetings,
  CompanionRecord,
  ConnectedSessionsTab,
  ConnectionExplorePage,
  DataSourcePage,
  DataSourcesPage,
  DeliverableViewer,
  FileLinkPage,
  DeliverablesPage,
  DeployPage,
  DeploymentPage,
  DeploymentsPage,
  FamilyChat,
  JobPage,
  MachineApprovalPage,
  MeetingsTab,
  MoreScreen,
  NewJobPage,
  OrgChartTab,
  ProjectsTab,
  PublishedPage,
  RecordTab,
  SavedResultPage,
  SavedResultsPage,
  SchedulesTab,
  SettingsTab,
  SharedTab,
  SkillsTab,
  SubagentBlueprintPage,
  SubagentDesignerPage,
  SubagentsTab,
  TasksTab,
  TodayScreen,
  TodosTab,
  UiGuidePage,
  WorkflowEditorPage,
  WorkflowRunPage,
  WorkflowsTab,
  page,
} from "./pages";
import { LegacyAgentSurfaceRedirect, RedirectCcSession, type AgentSection } from "./redirects";
// Apps (apps spec §10): Code › Apps, an app's page, /agent-dev/*.
import { AppPageRoute, AppsRoute, agentDevRoutes } from "./appsPages";

// "On their own" (the agents' free-time research): no sidebar row, reached from the morning line,
// Agent settings and Home's header; the page guards itself (initiatives_enabled).
const InitiativesTab = lazy(() => import("@/pages/customers/InitiativesTab").then((m) => ({ default: m.InitiativesTab })));
// Learned (what Lattice learned about the workspace, and what waits for a yes): no sidebar row, reached
// from the Monday clause and two muted links; the page guards itself (learning_enabled).
const LearnedTab = lazy(() => import("@/pages/customers/LearnedTab").then((m) => ({ default: m.LearnedTab })));

// Old per-surface URLs (with and without an agent) → agent workspace tabs.
const LEGACY_AGENT_SURFACES: Array<[path: string, section: AgentSection]> = [
  ["chat", "chat"],
  ["files", "files"],
  ["desktops", "desktop"],
  ["mail", "mail"],
  ["routines", "routines"],
  ["memories", "memory"],
];

/** Phone companion for an operator: its own shell and tabs. */
export function companionRoutes(customerId: string) {
  return (
    <Route path="companion" element={<CompanionShell customerId={customerId} />}>
      <Route index element={<Navigate to="today" replace />} />
      <Route path="today" element={page(<TodayScreen customerId={customerId} />)} />
      <Route path="record" element={page(<CompanionRecord customerId={customerId} />)} />
      <Route path="chat" element={page(<ChatScreen customerId={customerId} />)} />
      <Route path="chat/:agent" element={page(<ChatThread customerId={customerId} />)} />
      <Route path="memory/:agent" element={page(<CompanionMemory customerId={customerId} />)} />
      <Route path="meetings" element={page(<CompanionMeetings customerId={customerId} />)} />
      {/* Scheduling links: the same screen with Links picked (not mounted for a child). */}
      <Route path="meetings/links" element={page(<CompanionMeetings customerId={customerId} />)} />
      <Route path="more" element={page(<MoreScreen />)} />
      {/* Same panels as the desktop tabs; they lay out for a phone. */}
      <Route path="todos" element={page(<TodosTab />)} />
      {/* Tasks: also the iPhone app's web screen, which installed apps list whatever the switch says. */}
      <Route path="tasks" element={page(<TasksTab />)} />
      <Route path="tasks/:taskId" element={page(<TasksTab />)} />
      {/* On their own: where the morning line's link lands on a phone. */}
      <Route path="initiatives" element={page(<InitiativesTab />)} />
      {/* Learned: where the Monday clause's link lands on a phone (?view=&week= kept). */}
      <Route path="learned" element={page(<LearnedTab />)} />
      {/* Schedules: the desktop page, laid out for a phone (?id= opens one, as the bell links it). */}
      <Route path="schedules" element={page(<SchedulesTab />)} />
      <Route path="projects" element={page(<ProjectsTab />)} />
      <Route path="deliverables" element={page(<DeliverablesPage />)} />
      <Route path="deliverables/:agent/:id" element={page(<DeliverableViewer />)} />
      {/* An agent's link to a Corvus-storage file (PUBLIC_BASE_URL/f/<id>, opened on a phone). */}
      <Route path="f/:fileId" element={page(<FileLinkPage />)} />
      <Route path="skills" element={page(<SkillsTab />)} />
      <Route path="subagents" element={page(<SubagentsTab />)} />
      <Route path="subagents/new" element={page(<SubagentDesignerPage />)} />
      <Route path="subagents/:template/design" element={page(<SubagentDesignerPage />)} />
      <Route path="subagents/:template" element={page(<SubagentBlueprintPage />)} />
      <Route path="subagents/:template/:view" element={page(<SubagentBlueprintPage />)} />
      {/* The list, a run (List view + an inspector sheet, its report at /report) and a job;
          the editor, the brief form and the data sources are desktop pages. */}
      <Route path="workflows" element={page(<WorkflowsTab />)} />
      <Route path="workflows/runs/:runId" element={page(<WorkflowRunPage />)} />
      <Route path="workflows/runs/:runId/report" element={page(<WorkflowRunPage />)} />
      <Route path="workflows/jobs/new" element={<Navigate to="/companion/workflows?tab=activity" replace />} />
      <Route path="workflows/jobs/:runId" element={page(<JobPage />)} />
      <Route path="vault" element={page(<SharedTab />)} />
      <Route path="settings" element={page(<SettingsTab />)} />
      {/* A command waiting for the operator's OK (the bell and Corvus's own message link here). */}
      <Route path="machines/approvals/:id" element={page(<MachineApprovalPage />)} />
      {/* Apps: the list (?request=<id> opens a request's row) and one app's page. */}
      <Route path="apps" element={page(<AppsRoute />)} />
      <Route path="apps/:slug" element={page(<AppPageRoute back="/companion/apps" />)} />
    </Route>
  );
}

/**
 * A child (family role): the SAME companion shell — Today, Record, Chat,
 * Meetings — with no More tab and no operator routes mounted at all. The
 * server scopes every one of these to the child's own agent
 * (deps.Principal); this table is the second line, not the first.
 */
export function familyRoutes(customerId: string) {
  return (
    <>
      <Route path="companion" element={<CompanionShell customerId={customerId} />}>
        <Route index element={<Navigate to="today" replace />} />
        <Route path="today" element={page(<TodayScreen customerId={customerId} />)} />
        <Route path="record" element={page(<CompanionRecord customerId={customerId} />)} />
        <Route path="chat" element={page(<FamilyChat />)} />
        <Route path="meetings" element={page(<CompanionMeetings customerId={customerId} />)} />
      </Route>
      {agentDevRoutes()}
      <Route path="*" element={<Navigate to="/companion/today" replace />} />
    </>
  );
}

/** The desktop workspace: every page inside the sidebar + header shell. */
export function desktopRoutes(customerId: string) {
  return (
    <Route element={<CustomerShell customerId={customerId} />}>
      <Route index element={<LandingRedirect />} />
      <Route path="dashboard" element={page(<AgentsTab />)} />
      <Route path="projects" element={page(<ProjectsTab />)} />
      <Route path="meetings" element={page(<MeetingsTab />)} />
      <Route path="record" element={page(<RecordTab />)} />
      {/* Gated per workspace: the page itself checks the flag, because the
          URL stays typeable even when the nav item is hidden. */}
      <Route path="todos" element={page(<TodosTab />)} />
      <Route path="tasks" element={page(<TasksTab />)} />
      <Route path="tasks/:taskId" element={page(<TasksTab />)} />
      <Route path="initiatives" element={page(<InitiativesTab />)} />
      <Route path="learned" element={page(<LearnedTab />)} />
      <Route path="deliverables" element={page(<DeliverablesPage />)} />
      {/* A file in the viewer (an agent's link, PUBLIC_BASE_URL/f/<id>, resolves to it). */}
      <Route path="deliverables/:agent/:id" element={page(<DeliverableViewer />)} />
      <Route path="f/:fileId" element={page(<FileLinkPage />)} />

      <Route path="agents" element={page(<AgentWorkspaceTab />)} />
      <Route path="agents/:template" element={page(<AgentWorkspaceTab />)} />
      <Route path="agents/:template/:section" element={page(<AgentWorkspaceTab />)} />
      {LEGACY_AGENT_SURFACES.flatMap(([path, section]) => [
        <Route key={path} path={path} element={<LegacyAgentSurfaceRedirect section={section} />} />,
        <Route key={`${path}/t`} path={`${path}/:template`} element={<LegacyAgentSurfaceRedirect section={section} />} />,
      ])}
      {/* Handoffs is gone; old links land on Tasks. */}
      <Route path="handoffs" element={<Navigate to="/tasks" replace />} />
      <Route path="skills" element={page(<SkillsTab />)} />
      <Route path="subagents" element={page(<SubagentsTab />)} />
      {/* The designer (a new subagent, or a saved one reopened): matched before :template. */}
      <Route path="subagents/new" element={page(<SubagentDesignerPage />)} />
      <Route path="subagents/:template/design" element={page(<SubagentDesignerPage />)} />
      <Route path="subagents/:template" element={page(<SubagentBlueprintPage />)} />
      <Route path="subagents/:template/:view" element={page(<SubagentBlueprintPage />)} />
      {/* Workflows: the list, the live run view (its Report at `/report`, so
          a reload or a link lands on it), the data sources and one source, a
          job and the brief that starts one, the editor (new, or by slug);
          `runs/:runId`, `data`, `data/:name`, `saved-results[/:name]`, `published`, `deployments[/new|/:name]` and `jobs/…` are matched before `:slug`. */}
      <Route path="workflows" element={page(<WorkflowsTab />)} />
      <Route path="workflows/new" element={page(<WorkflowEditorPage />)} />
      <Route path="workflows/runs/:runId" element={page(<WorkflowRunPage />)} />
      <Route path="workflows/runs/:runId/report" element={page(<WorkflowRunPage />)} />
      <Route path="workflows/data" element={page(<DataSourcesPage />)} />
      <Route path="workflows/data/:name" element={page(<DataSourcePage />)} />
      <Route path="workflows/saved-results" element={page(<SavedResultsPage />)} />
      <Route path="workflows/saved-results/:name" element={page(<SavedResultPage />)} />
      <Route path="workflows/published" element={page(<PublishedPage />)} />
      {/* Deployments (workflows as APIs): the list, Deploy, one deployment; flag off → Published. */}
      <Route path="workflows/deployments" element={page(<DeploymentsPage />)} />
      <Route path="workflows/deployments/new" element={page(<DeployPage />)} />
      <Route path="workflows/deployments/:name" element={page(<DeploymentPage />)} />
      <Route path="workflows/jobs/new" element={page(<NewJobPage />)} />
      <Route path="workflows/jobs/:runId" element={page(<JobPage />)} />
      <Route path="workflows/:slug" element={page(<WorkflowEditorPage />)} />
      <Route path="org" element={page(<OrgChartTab />)} />
      <Route path="schedules" element={page(<SchedulesTab />)} />

      <Route path="shared" element={page(<SharedTab />)} />
      {/* "Code" groups sessions, connected sessions and deployments under
          one sidebar item; the CC* tabs are its children. */}
      <Route path="code" element={page(<CodeTab />)}>
        <Route index element={<Navigate to="sessions" replace />} />
        <Route path="sessions" element={page(<CCSessionsTab />)} />
        <Route path="sessions/:sessionId" element={page(<CCSessionsTab />)} />
        <Route path="connections" element={page(<ConnectedSessionsTab />)} />
        {/* Apps: the agent-built apps where the workspace has Apps on, today's list
            where it is off; the old Deployments address lands here. */}
        <Route path="apps" element={page(<AppsRoute />)} />
        <Route path="deployments" element={<Navigate to="/code/apps" replace />} />
      </Route>
      {/* One app's page: its own page, not a Code tab. */}
      <Route path="code/apps/:slug" element={page(<AppPageRoute />)} />
      {/* Old Code URLs, and /connections — the target of the backend's
          "connected workspace" notification. */}
      <Route path="cc-sessions" element={<Navigate to="/code/sessions" replace />} />
      <Route path="cc-sessions/:sessionId" element={<RedirectCcSession />} />
      <Route path="cc-deploys" element={<Navigate to="/code/apps" replace />} />
      <Route path="connections" element={<Navigate to="/code/connections" replace />} />
      <Route path="settings" element={page(<SettingsTab />)} />
      {/* A connection's Explore (what is inside it: tables or endpoints, Query or Try a call); desktop. */}
      <Route path="settings/connections/:id/explore" element={page(<ConnectionExplorePage />)} />
      {/* Machines live in Settings; a command waiting for the operator's OK has its own page. */}
      <Route path="machines" element={<Navigate to="/settings#machines" replace />} />
      <Route path="machines/approvals/:id" element={page(<MachineApprovalPage />)} />
      <Route path="ui-guide" element={page(<UiGuidePage />)} />

      {/* A customer hitting /admin is bounced; the backend's requireAdmin
          already refuses the data routes. */}
      <Route path="admin/*" element={<Navigate to="/dashboard" replace />} />
      {/* Absolute: a relative "dashboard" here appends forever when the
          shell is loaded under a path it does not own. */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Route>
  );
}
